import React, { useCallback } from "react";
import { FaRegFile } from "react-icons/fa";
import { isImage, isVideo } from "../../../../utils/helpers";
import { WhatsAppPreviewProps } from "./types";
import styled from "styled-components";

const WhatsAppPreview: React.FC<WhatsAppPreviewProps> = ({
  bodyValue,
  values,
  file,
}) => {
  const renderVideo = useCallback(() => {
    if (!file) return null;
    return (
      <video
        src={URL.createObjectURL(file)}
        style={{ maxWidth: "100%", height: 180, borderRadius: 8 }}
        controls
      />
    );
  }, [file]);

  const renderHeaderMedia = () => {
    if (!file) return null;

    if (isImage(values.header_value)) {
      return (
        <HeaderMedia>
          <img
            src={URL.createObjectURL(file)}
            alt="Header preview"
            style={{ 
              width: "100%", 
              height: "auto", 
              maxHeight: 180,
              borderRadius: "8px 8px 0 0",
              objectFit: "cover" 
            }}
          />
        </HeaderMedia>
      );
    }

    if (isVideo(values.header_value)) {
      return <HeaderMedia>{renderVideo()}</HeaderMedia>;
    }

    // Document preview
    return (
      <DocumentPreview>
        <FaRegFile size={24} color="#fff" />
        <DocumentName>{file?.name}</DocumentName>
      </DocumentPreview>
    );
  };

  const renderButtons = () => {
    if (values.haveButtons !== "yes" || !values.buttons?.length) return null;

    return (
      <ButtonsContainer>
        {values.buttons.map((button, index) => {
          if (!button.type || !button.text) return null;

          if (button.type === "QUICK_REPLY") {
            return (
              <QuickReplyButton key={index}>
                {button.text}
              </QuickReplyButton>
            );
          }

          if (button.type === "URL") {
            return (
              <ActionButton key={index}>
                🔗 {button.text}
              </ActionButton>
            );
          }

          if (button.type === "PHONE_NUMBER") {
            return (
              <ActionButton key={index}>
                📞 {button.text}
              </ActionButton>
            );
          }

          return null;
        })}
      </ButtonsContainer>
    );
  };

  return (
    <PreviewContainer>
      {/* WhatsApp Header */}
      <WhatsAppHeader>
        <BackIcon>
          <svg viewBox="0 0 24 24" focusable="false">
            <path
              fill="currentColor"
              d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
            />
          </svg>
        </BackIcon>
        <HeaderContent>
          <img
            src="./assets/img/whatsapp.svg"
            className="whatsapp-logo"
            alt="WhatsApp"
            width="20"
            height="20"
          />
          <span className="text-white ms-2">WhatsApp</span>
          <img
            src="./assets/img/verified.svg"
            className="verified ms-2"
            alt="Verified"
            width="12"
            height="12"
          />
        </HeaderContent>
        <DotsIcon>
          <svg
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            focusable="false"
            height="14"
            width="14"
          >
            <circle cx="12" cy="12" r="1"></circle>
            <circle cx="12" cy="5" r="1"></circle>
            <circle cx="12" cy="19" r="1"></circle>
          </svg>
        </DotsIcon>
      </WhatsAppHeader>

      {/* Chat Area */}
      <ChatArea>
        {(bodyValue?.length || values?.body) && (
          <MessageContainer>
            {/* Message Bubble */}
            <MessageBubble>
              {/* Header Media */}
              {renderHeaderMedia()}
              
              {/* Message Content */}
              <MessageContent $hasHeaderMedia={!!file}>
                {/* Body Text */}
                <MessageText>
                  {bodyValue || values?.body}
                </MessageText>
                
                {/* Footer Text */}
                {values.footer && (
                  <FooterText>
                    {values.footer}
                  </FooterText>
                )}
              </MessageContent>
            </MessageBubble>

            {/* Buttons Outside Message Bubble */}
            {renderButtons()}
          </MessageContainer>
        )}
      </ChatArea>

      {/* WhatsApp Input Footer */}
      <WhatsAppFooter>
        <InputContainer>
          <EmojiIcon>
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 496 512"
              focusable="false"
              height="1em"
              width="1em"
            >
              <path d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm80 152c17.7 0 32 14.3 32 32s-14.3 32-32 32-32-14.3-32-32 14.3-32 32-32zm-160 0c17.7 0 32 14.3 32 32s-14.3 32-32 32-32-14.3-32-32 14.3-32 32-32zm88 272h-16c-73.4 0-134-55-142.9-126-1.2-9.5 6.3-18 15.9-18h270c9.6 0 17.1 8.4 15.9 18-8.9 71-69.5 126-142.9 126z" />
            </svg>
          </EmojiIcon>
          <MessageInput type="text" placeholder="Message" readOnly />
          <AttachIcon>
            <svg height="1em" width="1em" viewBox="0 0 16 16">
              <path d="M10.404 5.11l-1.015-1.014-5.075 5.074c-0.841 0.841-0.841 2.204 0 3.044s2.204 0.841 3.045 0l6.090-6.089c1.402-1.401 1.402-3.673 0-5.074s-3.674-1.402-5.075 0l-6.394 6.393c-0.005 0.005-0.010 0.009-0.014 0.013-1.955 1.955-1.955 5.123 0 7.077s5.123 1.954 7.078 0c0.004-0.004 0.008-0.009 0.013-0.014l0.001 0.001 4.365-4.364-1.015-1.014-4.365 4.363c-0.005 0.004-0.009 0.009-0.013 0.013-1.392 1.392-3.656 1.392-5.048 0s-1.392-3.655 0-5.047c0.005-0.005 0.009-0.009 0.014-0.013l-0.001-0.001 6.395-6.393c0.839-0.84 2.205-0.84 3.045 0s0.839 2.205 0 3.044l-6.090 6.089c-0.28 0.28-0.735 0.28-1.015 0s-0.28-0.735 0-1.014l5.075-5.075z" />
            </svg>
          </AttachIcon>
          <CameraIcon>
            <svg height="1em" width="1em" viewBox="0 0 1024 1024">
              <path d="M864 260H728l-32.4-90.8a32.07 32.07 0 0 0-30.2-21.2H358.6c-13.5 0-25.6 8.5-30.1 21.2L296 260H160c-44.2 0-80 35.8-80 80v456c0 44.2 35.8 80 80 80h704c44.2 0 80-35.8 80-80V340c0-44.2-35.8-80-80-80zM512 716c-88.4 0-160-71.6-160-160s71.6-160 160-160 160 71.6 160 160-71.6 160-160 160zm-96-160a96 96 0 1 0 192 0 96 96 0 1 0-192 0z" />
            </svg>
          </CameraIcon>
        </InputContainer>
        <MicIcon>
          <img src="./assets/img/mic.png" alt="Voice message" />
        </MicIcon>
      </WhatsAppFooter>
    </PreviewContainer>
  );
};

// Styled Components
const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 350px;
  height: 600px;
  border: 1px solid #edf2f7;
  border-radius: 24px;
  overflow: hidden;
  position: sticky;
  top: 70px;
  background: #fff;
`;

const WhatsAppHeader = styled.div`
  background: #008069;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 60px;
`;

const BackIcon = styled.div`
  color: #fff;
  width: 14px;
  height: 14px;
  svg {
    width: 100%;
    height: 100%;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`;

const DotsIcon = styled.div`
  color: #fff;
  width: 14px;
  height: 14px;
`;

const ChatArea = styled.div`
  flex: 1;
  background-image: url("../../../../public/assets/img/wa-pattern.png");
  background-size: cover;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 0;
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  margin-top: auto;
  padding-bottom: 20px;
`;

const MessageBubble = styled.div`
  background-color: #dcf8c6;
  border-radius: 8px;
  max-width: 280px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const HeaderMedia = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DocumentPreview = styled.div`
  background: #128c7e;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DocumentName = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 500;
`;

const MessageContent = styled.div<{ $hasHeaderMedia: boolean }>`
  padding: ${props => props.$hasHeaderMedia ? '12px 16px 16px 16px' : '16px'};
`;

const MessageText = styled.div`
  color: #000;
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const FooterText = styled.div`
  color: #666;
  font-size: 12px;
  margin-top: 8px;
  font-style: italic;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 280px;
`;

const QuickReplyButton = styled.button`
  background: transparent;
  border: 1px solid #25d366;
  color: #25d366;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: default;
  transition: none;
  text-align: center;
  
  &:hover {
    background: transparent;
    border-color: #25d366;
  }
`;

const ActionButton = styled.button`
  background: #25d366;
  border: none;
  color: #fff;
  padding: 12px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: default;
  transition: none;
  text-align: center;
  
  &:hover {
    background: #25d366;
  }
`;

const WhatsAppFooter = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f0f0f0;
  gap: 8px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 20px;
  flex: 1;
  padding: 8px 16px;
  gap: 8px;
`;

const EmojiIcon = styled.div`
  color: #a0aec0;
  width: 24px;
  height: 24px;
`;

const MessageInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font-size: 14px;
  background: transparent;
  
  &::placeholder {
    color: #a0aec0;
  }
`;

const AttachIcon = styled.div`
  color: #a0aec0;
  width: 16px;
  height: 16px;
`;

const CameraIcon = styled.div`
  color: #a0aec0;
  width: 20px;
  height: 20px;
`;

const MicIcon = styled.div`
  width: 24px;
  height: 24px;
  
  img {
    width: 100%;
    height: 100%;
  }
`;

export default WhatsAppPreview;